import { useEffect, useRef, useState, useCallback } from "react";
import { useSearchParams, useNavigate, Link } from "react-router-dom";
import { SiteHeader } from "@/components/SiteHeader";
import { SiteFooter } from "@/components/SiteFooter";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { CheckCircle2, XCircle, Loader2, ArrowRight, Smartphone } from "lucide-react";
import { Button } from "@/components/ui/button";

// Mobile-app signups get a link with ?src=app so this page forwards the token into the
// Ronin Box app (which hosts its own verification screen) instead of verifying in-browser.
const APP_SCHEME = "roninbox";

export default function VerifyEmail() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const { refresh } = useAuth();
  const token = params.get("token");
  const appMode = params.get("src") === "app";
  // status: app (forward-to-app screen) | loading | success | error
  const [status, setStatus] = useState(appMode ? "app" : "loading");
  const [message, setMessage] = useState("");
  const verifyRanRef = useRef(false); // guard against React StrictMode double-invoke
  const appTriedRef = useRef(false);

  const runWebVerify = useCallback(async () => {
    if (!token) { setStatus("error"); setMessage("Missing verification token."); return; }
    setStatus("loading");
    try {
      const { data } = await api.get(`/auth/verify-email?token=${encodeURIComponent(token)}`);
      if (data?.verified) {
        setStatus("success");
        try { await refresh(); } catch { /* ignore */ }
      } else {
        setStatus("error"); setMessage("Verification failed — try requesting a new link.");
      }
    } catch (err) {
      setStatus("error");
      setMessage(err?.response?.data?.detail || "Couldn't verify — the link may have expired.");
    }
  }, [token, refresh]);

  const openApp = useCallback(() => {
    if (!token) return;
    window.location.href = `${APP_SCHEME}://verify-email?token=${encodeURIComponent(token)}`;
  }, [token]);

  useEffect(() => {
    if (!token) { setStatus("error"); setMessage("Missing verification token."); return; }
    if (verifyRanRef.current) return;
    verifyRanRef.current = true;
    if (appMode) {
      // Auto-attempt to launch the app once; the button remains for manual retry.
      if (!appTriedRef.current) { appTriedRef.current = true; openApp(); }
      return;
    }
    runWebVerify();
  }, [token]);

  return (
    <div className="min-h-screen bg-[#050505] text-white" data-testid="verify-email-page">
      <SiteHeader />
      <section className="max-w-xl mx-auto px-6 py-24 text-center">
        {status === "app" && (
          <>
            <Smartphone className="h-14 w-14 text-[#FF331F] mx-auto mb-4" />
            <h1 className="font-display text-4xl text-white uppercase tracking-tight mb-3" data-testid="verify-open-app-title">Open the app</h1>
            <p className="text-zinc-400 mb-8">We&apos;re launching Ronin Box to finish verifying your email. If it doesn&apos;t open automatically, tap below.</p>
            <Button onClick={openApp} className="bg-[#FF331F] hover:bg-[#FF5242] text-white font-bold uppercase tracking-[0.2em] text-xs px-8 py-6 rounded-sm" data-testid="verify-open-app-btn">
              Open Ronin Box <ArrowRight className="h-4 w-4 ml-2" />
            </Button>
            <div className="mt-6">
              <button onClick={runWebVerify} className="text-[11px] uppercase tracking-widest text-zinc-500 hover:text-white" data-testid="verify-web-fallback">
                Don&apos;t have the app? Verify in your browser →
              </button>
            </div>
          </>
        )}
        {status === "loading" && (
          <>
            <Loader2 className="h-12 w-12 text-[#FF331F] animate-spin mx-auto mb-4" />
            <h1 className="font-display text-3xl text-white uppercase tracking-tight">Verifying…</h1>
          </>
        )}
        {status === "success" && (
          <>
            <CheckCircle2 className="h-14 w-14 text-emerald-400 mx-auto mb-4" />
            <h1 className="font-display text-4xl text-white uppercase tracking-tight mb-3" data-testid="verify-success">You&apos;re in.</h1>
            <p className="text-zinc-400 mb-8">Ronin Box is unlocked. Time to throw hands.</p>
            <Button onClick={() => navigate("/ronin-box")} className="bg-[#FF331F] hover:bg-[#FF5242] text-white font-bold uppercase tracking-[0.2em] text-xs px-8 py-6 rounded-sm" data-testid="verify-to-ronin">
              Start your first round <ArrowRight className="h-4 w-4 ml-2" />
            </Button>
          </>
        )}
        {status === "error" && (
          <>
            <XCircle className="h-12 w-12 text-red-400 mx-auto mb-4" />
            <h1 className="font-display text-3xl text-white uppercase tracking-tight mb-3">Couldn&apos;t verify</h1>
            <p className="text-zinc-400 mb-8">{message}</p>
            <Link to="/ronin-box" className="text-[#FF331F] font-bold uppercase tracking-widest text-sm">← Back to Ronin Box</Link>
          </>
        )}
      </section>
      <SiteFooter />
    </div>
  );
}
